import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import type { ShareActionsOptions } from '#src/app/shared/hooks/share';
import {
	buildFacebookTrackingEvents,
	openFacebookShare,
	trackFacebookShareSuccess,
} from '#src/app/shared/share/facebook';
import type { getShareUrlInfo } from '#src/app/shared/share/utils';
import { openUrlWithHiddenAnchor } from '#src/app/shared/share/utils';

import type { FacebookPostSheetCopyTrigger } from './share/tracking';
import { useFacebookPostSheetTracking } from './share/tracking';
import type { FacebookPostSheetTap } from './useFacebookPostSheet';

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The same share options the pipeline builds (`getAction.tsx`), so the sheet's events match
 * production.
 */
export function buildSheetShareOptions(
	{ getTrackingData, trackingEventName, config }: ShareActionsOptions,
	trackingProperties: Readonly<Record<string, string>>,
	tap: FacebookPostSheetTap,
) {
	return {
		// Same merge order as the pipeline: page data, then the link's share id, then the tap's override.
		trackingData: { ...getTrackingData('facebook'), ...trackingProperties, ...tap.trackingData },
		trackingEvents: buildFacebookTrackingEvents(trackingEventName),
		mode: config?.facebook?.mode,
	};
}

type HandoffParams = {
	payload: string | null;
	urlInfo: ReturnType<typeof getShareUrlInfo> | null;
	shareOptions: ShareActionsOptions;
	copy: (trigger: FacebookPostSheetCopyTrigger) => Promise<boolean>;
};

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The feed post: `postToFeed`, the `variant_1` Post button and the `variant_2` Continue tap with the
 * feed picked, which copies (when the Facebook tap's copy did not succeed) and opens the composer in one
 * tap.
 */
export function useFeedHandoff({ payload, urlInfo, shareOptions, copy }: HandoffParams) {
	const utilityContext = useUtilityContext();
	const track = useTracking();

	const openFeedComposer = useCallback(
		(tap: FacebookPostSheetTap) => {
			if (!payload || !urlInfo) return;
			// No `onBeforeShare` here, unlike the pipeline: `tapFacebook`, at the Facebook tap that opened the
			// sheet, already called it, and that earlier call is what cancels a pending copy-link redirect.
			const { onSuccess, onFailure } = shareOptions;

			// `openFacebookShare` reaches `window.open` before its first await, so the open stays inside the tap.
			const opening = openFacebookShare(
				urlInfo.url,
				buildSheetShareOptions(shareOptions, urlInfo.trackingProperties, tap),
				utilityContext,
				track,
			);

			void (async () => {
				const opened = await opening;
				await (opened ? onSuccess : onFailure)?.('facebook');
			})();
		},
		[payload, urlInfo, shareOptions, utilityContext, track],
	);

	const postToFeed = useCallback(
		(tap: FacebookPostSheetTap) => {
			if (!payload || !urlInfo) return;
			// Both calls start synchronously, inside the tap: the clipboard write runs before the first
			// await in `copyToClipboard`, and the composer open follows it. After a successful copy at the
			// Facebook tap, the copy writes nothing and records nothing.
			void copy('post_cta');
			openFeedComposer(tap);
		},
		[payload, urlInfo, copy, openFeedComposer],
	);

	return { postToFeed };
}

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The `variant_2` two-tap group handoff: a card tap copies, and the group CTA opens the group.
 */
export function useGroupHandoff({ payload, urlInfo, shareOptions, copy }: HandoffParams) {
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const { trackGroupCardTapped } = useFacebookPostSheetTracking();

	const selectGroup = useCallback(
		async (group: FacebookGroupRecommendation) => {
			trackGroupCardTapped(group.url);
			return copy('group_card');
		},
		[trackGroupCardTapped, copy],
	);

	const postToGroup = useCallback(
		(tap: FacebookPostSheetTap, group: FacebookGroupRecommendation) => {
			if (!payload || !urlInfo) return;
			// Same as `postToFeed`: `tapFacebook` already called `onBeforeShare` at the Facebook tap.
			const { trackingData, trackingEvents } = buildSheetShareOptions(shareOptions, urlInfo.trackingProperties, tap);

			// The Facebook tap or a card tap copied already, so this writes only after those copies failed.
			// Then the group opens, first thing after the copy in the tap, so mobile Safari does not block it.
			void copy('group_cta');
			// Opened the way the production Facebook share opens its dialog, and counted as a success the
			// same way. `window.open` cannot tell a blocked open apart: iOS returns null when a Facebook
			// group link hands off to the Facebook app, though the group opened.
			openUrlWithHiddenAnchor(group.url, { newTab: true });

			trackFacebookShareSuccess(
				trackingEvents.success,
				{ ...trackingData, facebook_post_sheet_destination: 'group', recommendation_group_url: group.url },
				utilityContext,
				track,
			);
			void shareOptions.onSuccess?.('facebook');
		},
		[payload, urlInfo, shareOptions, copy, utilityContext, track],
	);

	return { selectGroup, postToGroup };
}
